/**
 * ============================================================
 * System       : CSAF Alert Finance
 * Objective    : Automated Reserve Fund Notification System
 * File         : frontend/src/pages/Notifications.jsx
 * Description  : Alert day settings and auto mode
 * ------------------------------------------------------------
 * Developer    : Chokchai Thainkhao (Head of IT)
 * Co-Author    : AI Assisted (Claude) — โครงสร้างและฟังก์ชันหลัก
 * Modified &
 *  Integrated  : Chokchai Thainkhao
 * Version      : 1.0.0
 * Date         : May 2026
 * Copyright    : © 2026 Chokchai T. All rights reserved.
 * ============================================================
 */

import React, { useEffect, useState, useCallback } from 'react';
import { getSettings, saveSettings } from '../api';
import { useToast } from '../App';

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
        <h3 className="font-semibold text-gray-700">{title}</h3>
      </div>
      <div className="px-6 py-5 space-y-4">{children}</div>
    </div>
  );
}

function Toggle({ checked, onChange, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div
        onClick={() => onChange(!checked)}
        style={{
          position: 'relative', width: 44, height: 24, borderRadius: 12, cursor: 'pointer',
          backgroundColor: checked ? '#1D9E75' : '#d1d5db',
          transition: 'background-color .2s', flexShrink: 0,
        }}
      >
        <span style={{
          position: 'absolute', top: 3, left: checked ? 23 : 3,
          width: 18, height: 18, borderRadius: '50%', backgroundColor: '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,.25)', transition: 'left .2s', display: 'block',
        }} />
      </div>
      <span style={{ fontSize: 14, color: '#374151' }}>{label}</span>
      <span style={{ fontSize: 12, fontWeight: 600, color: checked ? '#1D9E75' : '#9ca3af' }}>
        {checked ? 'เปิด' : 'ปิด'}
      </span>
    </div>
  );
}

function NumberInput({ label, value, onChange, unit = 'วัน', max = 365 }) {
  return (
    <div className="flex items-center gap-4">
      <label className="text-sm font-medium text-gray-700 w-40">{label}</label>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        min={1} max={max}
        className="w-24 border border-gray-200 rounded-lg px-3 py-2 text-sm text-center focus:outline-none focus:ring-2 focus:ring-primary/30"
      />
      <span className="text-sm text-gray-400">{unit}</span>
    </div>
  );
}

export default function Notifications({ setNotifCount }) {
  const [settings, setSettings] = useState({
    alert1_days: '5',
    alert2_days: '10',
    alert3_days: '15',
    auto_mode: '0',
    email_enabled: '1',
    annual_alert_enabled: '1',
    annual_alert1_day: '1',
    annual_alert2_day: '15',
    annual_alert3_day: '25',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { addToast } = useToast();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSettings();
      setSettings((prev) => ({ ...prev, ...res.data }));
      setNotifCount(0);
    } catch (e) {
      if (!e.response) {
        addToast('error', 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้');
      } else {
        addToast('error', e.response?.data?.detail || 'โหลดการตั้งค่าไม่สำเร็จ');
      }
    } finally {
      setLoading(false);
    }
  }, [addToast, setNotifCount]);

  useEffect(() => { loadData(); }, [loadData]);

  const update = (key) => (val) => setSettings((prev) => ({ ...prev, [key]: String(val) }));

  const handleSave = async () => {
    // Validate alert day ordering
    const d1 = parseInt(settings.alert1_days);
    const d2 = parseInt(settings.alert2_days);
    const d3 = parseInt(settings.alert3_days);
    if (d1 <= 0 || d2 <= 0 || d3 <= 0) {
      addToast('error', 'กำหนดวัน Alert ต้องมากกว่า 0');
      return;
    }
    if (d1 >= d2 || d2 >= d3) {
      addToast('error', 'วัน Alert 1 < Alert 2 < Alert 3 เท่านั้น');
      return;
    }

    // Validate annual (สำรองปี) day-of-month ordering
    const a1 = parseInt(settings.annual_alert1_day);
    const a2 = parseInt(settings.annual_alert2_day);
    const a3 = parseInt(settings.annual_alert3_day);
    if (a1 < 1 || a1 > 31 || a2 < 1 || a2 > 31 || a3 < 1 || a3 > 31) {
      addToast('error', 'วันที่แจ้งเตือนสำรองปีต้องอยู่ระหว่าง 1-31');
      return;
    }
    if (a1 >= a2 || a2 >= a3) {
      addToast('error', 'วันที่สำรองปี ครั้งที่ 1 < ครั้งที่ 2 < ครั้งที่ 3 เท่านั้น');
      return;
    }

    setSaving(true);
    try {
      await saveSettings(settings);
      addToast('success', 'บันทึกการตั้งค่าสำเร็จ');
    } catch (e) {
      if (!e.response) {
        addToast('error', 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้');
      } else {
        addToast('error', e.response?.data?.detail || 'บันทึกการตั้งค่าไม่สำเร็จ');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-2xl">
      <h2 className="text-lg font-semibold text-gray-800">การแจ้งเตือน</h2>

      <Section title="การแจ้งเตือนอัตโนมัติ">
        <Toggle
          checked={settings.auto_mode === '1'}
          onChange={(v) => update('auto_mode')(v ? '1' : '0')}
          label="Auto Mode — ส่ง Alert อัตโนมัติทุกวัน 08:00 น."
        />
        <Toggle
          checked={settings.email_enabled === '1'}
          onChange={(v) => update('email_enabled')(v ? '1' : '0')}
          label="เปิดใช้งานการส่งอีเมล"
        />
      </Section>

      <Section title="กำหนดวัน Alert">
        <NumberInput label="Alert ครั้งที่ 1" value={settings.alert1_days} onChange={update('alert1_days')} />
        <NumberInput label="Alert ครั้งที่ 2" value={settings.alert2_days} onChange={update('alert2_days')} />
        <NumberInput label="Alert ครั้งที่ 3" value={settings.alert3_days} onChange={update('alert3_days')} />
        <p className="text-xs text-gray-400 mt-2">
          นับจากวันที่สำรองจ่าย ระบบจะส่ง Alert เมื่อครบกำหนดวัน (ตามลำดับ 1→2→3)
        </p>
      </Section>

      <Section title="การแจ้งเตือนสำรองปี">
        <Toggle
          checked={settings.annual_alert_enabled === '1'}
          onChange={(v) => update('annual_alert_enabled')(v ? '1' : '0')}
          label="เปิดใช้งานการแจ้งเตือนสำรองปี"
        />
        <NumberInput label="แจ้งเตือน ครั้งที่ 1" value={settings.annual_alert1_day} onChange={update('annual_alert1_day')} unit="ธ.ค." max={31} />
        <NumberInput label="แจ้งเตือน ครั้งที่ 2" value={settings.annual_alert2_day} onChange={update('annual_alert2_day')} unit="ธ.ค." max={31} />
        <NumberInput label="แจ้งเตือน ครั้งที่ 3" value={settings.annual_alert3_day} onChange={update('annual_alert3_day')} unit="ธ.ค." max={31} />
        <p className="text-xs text-gray-400 mt-2">
          สำหรับรายการที่เลือกประเภท "สำรองปี" ในหน้ารายการสำรองจ่าย — ระบบจะส่งอีเมล/LINE แจ้งเตือนในวันที่กำหนดของเดือนธันวาคม
          <strong> ปีเดียวกับวันที่สำรองจ่าย</strong> ของรายการนั้น แทนการแจ้งเตือนแบบนับวันด้านบน (ใช้แทนกัน ไม่ใช่เพิ่มเติม)
        </p>
      </Section>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors disabled:opacity-60"
        >
          {saving ? 'กำลังบันทึก...' : 'บันทึกการตั้งค่า'}
        </button>
      </div>
    </div>
  );
}
